export const OFFLINE_SCHEMA_VERSION = 3;

export const SYNC_STATE = Object.freeze({
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  RETRY_SCHEDULED: "RETRY_SCHEDULED",
  FAILED_RECOVERABLE: "FAILED_RECOVERABLE",
  FAILED_PERMANENT: "FAILED_PERMANENT",
  SYNCED: "SYNCED",
  SUPERSEDED: "SUPERSEDED",
});

export const MAX_AUTOMATIC_ATTEMPTS = 8;
export const PROCESSING_STALE_MS = 60_000;
export const MAX_RETRY_DELAY_MS = 5 * 60_000;
const BASE_RETRY_DELAY_MS = 2_000;

const PENDING_STATES = new Set([
  SYNC_STATE.PENDING,
  SYNC_STATE.PROCESSING,
  SYNC_STATE.RETRY_SCHEDULED,
]);
const FAILED_STATES = new Set([
  SYNC_STATE.FAILED_RECOVERABLE,
  SYNC_STATE.FAILED_PERMANENT,
]);

function iso(value, fallback = null) {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toISOString();
}

function attemptCount(item) {
  const value = Number(item?.attemptCount ?? item?.retryCount ?? 0);
  return Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}

function legacyState(value) {
  return String(value || "PENDING").trim().toUpperCase();
}

function usableBlob(file) {
  return file && typeof file.size === "number" && file.size > 0;
}

function legacyValidation(item, kind) {
  if (!item?.id) return "Identitas data lokal tidak tersedia.";
  if (kind === "media") {
    const oldState = legacyState(item.syncStatus || item.status);
    const confirmedWithoutBlob = ["STORED", "SYNCED"].includes(oldState)
      && Boolean(item.serverMediaId)
      && (item.blobRetained === false || Boolean(item.blobCleanedAt));
    if (!usableBlob(item.file) && !confirmedWithoutBlob) return "File foto lokal tidak tersedia atau kosong.";
    if (!item.mediaKind) return "Jenis foto lokal belum tercatat.";
    return null;
  }
  if (!item.path || !item.method) return "Tujuan sinkronisasi data lama tidak lengkap.";
  if (!item.payload || typeof item.payload !== "object") return "Isi data lama tidak dapat dibaca.";
  if (!(item.idempotencyKey || item.payload.idempotencyKey || item.localTransactionId)) {
    return "Identitas transaksi data lama tidak tersedia.";
  }
  return null;
}

export function computeRetryDelayMs(attempt, random = Math.random) {
  const safeAttempt = Math.max(1, Math.floor(Number(attempt) || 1));
  const exponential = Math.min(MAX_RETRY_DELAY_MS, BASE_RETRY_DELAY_MS * (2 ** (safeAttempt - 1)));
  const jitter = 0.75 + (Math.min(1, Math.max(0, Number(random()) || 0)) * 0.5);
  return Math.min(MAX_RETRY_DELAY_MS, Math.round(exponential * jitter));
}

export function normalizeLegacySyncItem(item = {}, { kind = "queue", now = new Date().toISOString() } = {}) {
  const currentNow = iso(now, new Date().toISOString());
  const createdAt = iso(item.createdAt, currentNow);
  const attempts = attemptCount(item);
  const validationError = legacyValidation(item, kind);
  const oldState = legacyState(item.syncStatus || item.status);
  let syncStatus = oldState;
  let recoverability = item.recoverability || null;
  let errorCategory = item.errorCategory || null;
  let nextAttemptAt = iso(item.nextAttemptAt);
  let processingStartedAt = iso(item.processingStartedAt);
  let lastErrorMessage = item.lastErrorMessage || item.errorMessage || null;

  if (validationError) {
    syncStatus = SYNC_STATE.FAILED_PERMANENT;
    recoverability = "REQUIRES_REMOVAL";
    errorCategory = "LEGACY_INVALID";
    lastErrorMessage = validationError;
    nextAttemptAt = null;
    processingStartedAt = null;
  } else if (["STORED", "SYNCED"].includes(oldState)) {
    syncStatus = SYNC_STATE.SYNCED;
    recoverability = null;
    errorCategory = null;
    nextAttemptAt = null;
    processingStartedAt = null;
  } else if (["SYNCING", "PROCESSING"].includes(oldState)) {
    const startedAt = iso(item.processingStartedAt || item.lastAttemptAt);
    const stillActive = startedAt
      && new Date(currentNow).getTime() - new Date(startedAt).getTime() < PROCESSING_STALE_MS;
    if (stillActive) {
      syncStatus = SYNC_STATE.PROCESSING;
      processingStartedAt = startedAt;
    } else {
      syncStatus = SYNC_STATE.RETRY_SCHEDULED;
      recoverability = "AUTOMATIC";
      errorCategory = "INTERRUPTED";
      lastErrorMessage = "Sinkronisasi sebelumnya terputus saat aplikasi ditutup.";
      nextAttemptAt = currentNow;
      processingStartedAt = null;
    }
  } else if (["FAILED", "FAILED_RECOVERABLE"].includes(oldState)) {
    syncStatus = SYNC_STATE.FAILED_RECOVERABLE;
    recoverability = item.recoverability || "MANUAL_RETRY";
    errorCategory = item.errorCategory || "LEGACY_FAILURE";
    lastErrorMessage = lastErrorMessage || "Data lama belum berhasil disinkronkan.";
    nextAttemptAt = null;
    processingStartedAt = null;
  } else if (oldState === "FAILED_PERMANENT") {
    syncStatus = SYNC_STATE.FAILED_PERMANENT;
    recoverability = item.recoverability || "REQUIRES_REMOVAL";
    nextAttemptAt = null;
    processingStartedAt = null;
  } else if (oldState === "SUPERSEDED") {
    syncStatus = SYNC_STATE.SUPERSEDED;
    nextAttemptAt = null;
    processingStartedAt = null;
  } else if (oldState === "RETRY_SCHEDULED") {
    syncStatus = SYNC_STATE.RETRY_SCHEDULED;
    recoverability = item.recoverability || "AUTOMATIC";
    nextAttemptAt = nextAttemptAt || currentNow;
    processingStartedAt = null;
  } else {
    syncStatus = SYNC_STATE.PENDING;
    recoverability = "AUTOMATIC";
    nextAttemptAt = nextAttemptAt || currentNow;
    processingStartedAt = null;
  }

  const idempotencyKey = item.idempotencyKey
    || item.payload?.idempotencyKey
    || item.localTransactionId
    || item.id;

  return {
    ...item,
    schemaVersion: OFFLINE_SCHEMA_VERSION,
    localTransactionId: item.localTransactionId || item.id,
    idempotencyKey,
    createdAt,
    updatedAt: currentNow,
    attemptCount: attempts,
    retryCount: attempts,
    syncStatus,
    recoverability,
    errorCategory,
    lastErrorMessage,
    errorMessage: lastErrorMessage,
    lastHttpStatus: Number(item.lastHttpStatus || 0) || null,
    lastErrorCode: item.lastErrorCode || null,
    lastAttemptAt: iso(item.lastAttemptAt),
    nextAttemptAt,
    processingStartedAt,
  };
}

export function classifySyncFailure({ status = null, code = null, message = null, attempt = 1, now = new Date().toISOString(), random = Math.random } = {}) {
  const httpStatus = Number(status || 0) || null;
  const safeCode = String(code || "").trim().toUpperCase() || null;
  const safeMessage = String(message || "").trim() || "Sinkronisasi belum berhasil.";

  if (httpStatus === 401) {
    return {
      syncStatus: SYNC_STATE.FAILED_RECOVERABLE,
      errorCategory: "AUTHENTICATION",
      recoverability: "LOGIN_REQUIRED",
      lastErrorMessage: "Sesi Anda berakhir. Masuk kembali, lalu coba sinkronkan.",
      nextAttemptAt: null,
    };
  }

  if ([400, 403, 404, 409, 413, 415, 422].includes(httpStatus)) {
    const category = httpStatus === 413
      ? "FILE_TOO_LARGE"
      : httpStatus === 415
        ? "UNSUPPORTED_MEDIA"
        : httpStatus === 403
          ? "FORBIDDEN"
          : httpStatus === 404
            ? "NOT_FOUND"
            : httpStatus === 409
              ? "CONFLICT"
              : "VALIDATION";
    return {
      syncStatus: SYNC_STATE.FAILED_PERMANENT,
      errorCategory: category,
      recoverability: "REQUIRES_REVIEW",
      lastErrorMessage: safeMessage,
      nextAttemptAt: null,
    };
  }

  if (Number(attempt) >= MAX_AUTOMATIC_ATTEMPTS) {
    return {
      syncStatus: SYNC_STATE.FAILED_RECOVERABLE,
      errorCategory: httpStatus === 429 ? "RATE_LIMIT" : httpStatus && httpStatus >= 500 ? "SERVER" : "NETWORK",
      recoverability: "MANUAL_RETRY",
      lastErrorMessage: "Sinkronisasi beberapa kali gagal. Data tetap aman dan dapat dicoba lagi.",
      nextAttemptAt: null,
    };
  }

  const nextAttemptAt = new Date(new Date(now).getTime() + computeRetryDelayMs(attempt, random)).toISOString();
  return {
    syncStatus: SYNC_STATE.RETRY_SCHEDULED,
    errorCategory: httpStatus === 429 ? "RATE_LIMIT" : httpStatus && httpStatus >= 500 ? "SERVER" : "NETWORK",
    recoverability: "AUTOMATIC",
    lastErrorMessage: safeMessage,
    nextAttemptAt,
    lastErrorCode: safeCode,
  };
}

export function isPendingSyncState(state) {
  return PENDING_STATES.has(String(state || "").toUpperCase());
}

export function isFailedSyncState(state) {
  return FAILED_STATES.has(String(state || "").toUpperCase());
}

export function isSyncItemDue(item, now = Date.now()) {
  const state = String(item?.syncStatus || "").toUpperCase();
  if (state === SYNC_STATE.PENDING) return true;
  if (state === SYNC_STATE.PROCESSING) {
    const startedAt = new Date(item.processingStartedAt || item.lastAttemptAt || 0).getTime();
    return !Number.isFinite(startedAt) || startedAt + PROCESSING_STALE_MS <= Number(now);
  }
  if (state !== SYNC_STATE.RETRY_SCHEDULED) return false;
  const due = new Date(item.nextAttemptAt || 0).getTime();
  return !Number.isFinite(due) || due <= Number(now);
}

export function syncFailureLabel(item) {
  const category = String(item?.errorCategory || "").toUpperCase();
  const errorCode = String(item?.lastErrorCode || "").toUpperCase();
  if (["DRIVER_ROUTE_VERSION_CONFLICT", "DRIVER_ROUTE_STALE", "ROUTE_PREVIEW_STOP_SET_STALE"].includes(errorCode)) {
    return "Urutan rute sudah berubah. Buka ulang pengaturan rute dan susun kembali dari data terbaru.";
  }
  if (["DRIVER_LOADING_ROUTE_CONFLICT", "DRIVER_LOADING_STALE"].includes(errorCode)) {
    return "Daftar pekerjaan berubah. Buka ulang Fleet Order dan periksa checklist loading.";
  }
  if (errorCode === "DRIVER_SYNC_DEPENDENCY_BLOCKED") {
    return "Data ini menunggu masalah urutan rute atau checklist sebelumnya diselesaikan.";
  }
  if (category === "AUTHENTICATION") return "Sesi berakhir. Masuk kembali lalu coba lagi.";
  if (category === "FILE_TOO_LARGE") return "Foto terlalu besar untuk dikirim.";
  if (category === "UNSUPPORTED_MEDIA") return "Format foto tidak didukung.";
  if (category === "NETWORK") return "Koneksi terputus saat sinkronisasi.";
  if (category === "SERVER") return "Server belum dapat menerima data.";
  if (category === "RATE_LIMIT") return "Terlalu banyak percobaan. Tunggu sebentar lalu coba lagi.";
  if (category === "LEGACY_INVALID") return item.lastErrorMessage || "Data lama perlu diperiksa.";
  return item?.lastErrorMessage || "Data belum berhasil disinkronkan.";
}
